import { isMaintenanceInterruptionError } from '@/maintenance/maintenanceErrors';

export type DatasetCheckpointField =
  | 'serverLineage'
  | 'version'
  | 'revision'
  | 'count'
  | 'updatedAt'
  | 'digest';

export interface DatasetCheckpoint {
  serverLineage?: string | null;
  version?: string | number | null;
  revision?: number | null;
  count?: number | null;
  updatedAt?: string | null;
  digest?: string | null;
}

export interface DatasetCheckpointComparison {
  status: 'unchanged' | 'changed' | 'unverifiable';
  comparedFields: DatasetCheckpointField[];
  changedFields: DatasetCheckpointField[];
}

export type DatasetPresentationPhase =
  | 'empty'
  | 'hydrating'
  | 'verifying'
  | 'syncing'
  | 'partial'
  | 'fresh'
  | 'stale'
  | 'offline'
  | 'error';

export interface DatasetPresentationState {
  phase: DatasetPresentationPhase;
  source: 'none' | 'cache' | 'network';
  fetchedCount: number;
  totalCount: number | null;
  complete: boolean;
  verifiedAt: number | null;
  error: string | null;
}

export const EMPTY_DATASET_PRESENTATION_STATE: DatasetPresentationState = {
  phase: 'empty',
  source: 'none',
  fetchedCount: 0,
  totalCount: null,
  complete: false,
  verifiedAt: null,
  error: null,
};

export type DatasetSyncReason =
  | 'initial'
  | 'interval'
  | 'visible'
  | 'focus'
  | 'online'
  | 'service-recovered'
  | 'realtime'
  // This reader's own write landed (I-0315): a view checks, as it does for somebody else's change.
  | 'write'
  | 'manual'
  | 'retry'
  | 'follow-up';

export const DATASET_SYNC_RECOVERY_EVENT = 'featit:dataset-sync-recovery';

/**
 * How long a single pass may run before it is presumed lost.
 *
 * Longer than any deadline below it — the transport gives up at `API_TIMEOUT_MS` — because this is
 * the backstop for a pass that hangs for a reason no transport deadline covers: an IndexedDB
 * transaction that never fires its callback, a promise chain that forgot to settle, a hydrate
 * awaiting something that no longer exists. Whatever the cause, the controller must not keep the
 * "a pass is in flight" latch closed forever, because every later trigger — the interval, a tab
 * becoming visible, a manual retry — is refused while it is held.
 */
export const DATASET_SYNC_STALL_TIMEOUT_MS = 30_000;

/**
 * Recovery cadence after a failed pass, in order, holding at the last entry.
 *
 * Escalating rather than fixed so a blip heals in seconds while an outage that lasts an hour is not
 * hammered once a second for an hour.
 */
export const DATASET_SYNC_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000];

/**
 * Recovery cadence after a pass that ran out of time, in order, holding at the last entry (I-1305).
 *
 * A read the server stopped at its limit, or that the transport gave up on, costs the server the
 * same time again each time it is asked. At the cadence above, which is a blip's, one slow view was
 * asked 28 times in ten minutes, and each abandoned request left its statement running. So it is
 * asked again after half a minute, then minutes apart, and in between nothing unattended asks it:
 * not the poll, not the doorbell, not focus. Somebody pressing "Try again" still does.
 */
export const DATASET_SYNC_SLOW_READ_RETRY_DELAYS_MS = [30_000, 120_000, 300_000, 900_000];

/** The passes a reader, or the retry this controller scheduled, asks for: never held back. */
const ASKED_FOR_REASONS: ReadonlySet<DatasetSyncReason> = new Set(['manual', 'retry']);

/** A pass that outlived {@link DATASET_SYNC_STALL_TIMEOUT_MS} and was abandoned. */
export class DatasetSyncStallError extends Error {
  readonly isDatasetSyncStall = true;

  constructor(
    readonly reason: DatasetSyncReason,
    readonly timeoutMs: number,
  ) {
    super('The sync did not answer in time and was abandoned.');
    this.name = 'DatasetSyncStallError';
  }
}

export function isDatasetSyncStallError(error: unknown): error is DatasetSyncStallError {
  return Boolean((error as DatasetSyncStallError | null | undefined)?.isDatasetSyncStall);
}

interface BrowserEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface BrowserDocumentTarget extends BrowserEventTarget {
  visibilityState: DocumentVisibilityState;
}

type DatasetIntervalHandle = ReturnType<typeof setInterval>;
type DatasetSetInterval = (callback: () => void, intervalMs: number) => DatasetIntervalHandle;
type DatasetClearInterval = (handle: DatasetIntervalHandle) => void;
type DatasetTimeoutHandle = ReturnType<typeof setTimeout>;
type DatasetSetTimeout = (callback: () => void, delayMs: number) => DatasetTimeoutHandle;
type DatasetClearTimeout = (handle: DatasetTimeoutHandle) => void;

interface DatasetSyncControllerOptions {
  hydrate?: () => Promise<void> | void;
  verify: (reason: DatasetSyncReason) => Promise<void> | void;
  intervalMs: number;
  documentTarget?: BrowserDocumentTarget | null;
  windowTarget?: BrowserEventTarget | null;
  setIntervalFn?: DatasetSetInterval;
  clearIntervalFn?: DatasetClearInterval;
  setTimeoutFn?: DatasetSetTimeout;
  clearTimeoutFn?: DatasetClearTimeout;
  /**
   * Ask the server without waiting for the local read to finish first.
   *
   * By default a cache read gates the first remote pass, so nothing is asked of the server until
   * local storage answers. That ordering buys a guarantee — cached data can never land on top of
   * fresher data, because it always lands first — and pays for it in latency the page cannot
   * bound: whatever is slow about local storage on a given machine becomes the time to first row,
   * even when the network would have answered in a fraction of it. A cache is meant to make a page
   * faster and this lets it only make it slower.
   *
   * Set this where the loaders enforce the guarantee themselves — refusing to apply a cached
   * payload once a network answer has been applied — and the two run as a race that the freshest
   * answer wins.
   */
  raceHydrateWithVerify?: boolean;
  /** Overrides {@link DATASET_SYNC_STALL_TIMEOUT_MS}; a non-positive value disables the watchdog. */
  stallTimeoutMs?: number;
  /** Overrides {@link DATASET_SYNC_RETRY_DELAYS_MS}; an empty list disables unattended recovery. */
  retryDelaysMs?: number[];
  /**
   * Whether a failed pass ran out of time (I-1305). Such a pass is retried on
   * {@link DATASET_SYNC_SLOW_READ_RETRY_DELAYS_MS}, and until its retry is due no unattended pass
   * (poll, doorbell, focus, coming back online) is made.
   */
  isSlowRead?: (error: unknown) => boolean;
  /** Overrides {@link DATASET_SYNC_SLOW_READ_RETRY_DELAYS_MS}. */
  slowReadRetryDelaysMs?: number[];
  /**
   * `retryInMs` is how long until this controller asks again by itself, or null when it will not
   * (no retry steps configured, or stopped).
   */
  onError?: (error: unknown, reason: DatasetSyncReason, retryInMs: number | null) => void;
  /**
   * Called once when a pass succeeds after one or more failed ones, with how many it took. The
   * counterpart to `onError`: something that reported a failure has to be told it is over, or the
   * reader keeps looking at a stale explanation of a problem that has since repaired itself.
   */
  onRecovered?: (afterFailures: number) => void;
}

const CHECKPOINT_FIELDS: DatasetCheckpointField[] = [
  'serverLineage',
  'version',
  'revision',
  'count',
  'updatedAt',
  'digest',
];

export function compareDatasetCheckpoints(
  applied: DatasetCheckpoint | null | undefined,
  remote: DatasetCheckpoint | null | undefined,
): DatasetCheckpointComparison {
  if (!applied || !remote) {
    return { status: 'unverifiable', comparedFields: [], changedFields: [] };
  }

  const comparedFields = CHECKPOINT_FIELDS.filter((field) =>
    field === 'serverLineage'
      ? hasCheckpointValue(applied[field]) || hasCheckpointValue(remote[field])
      : hasCheckpointValue(applied[field]) && hasCheckpointValue(remote[field]),
  );
  if (comparedFields.length === 0) {
    return { status: 'unverifiable', comparedFields, changedFields: [] };
  }

  const changedFields = comparedFields.filter(
    (field) =>
      normalizeCheckpointValue(field, applied[field]) !==
      normalizeCheckpointValue(field, remote[field]),
  );
  return {
    status: changedFields.length > 0 ? 'changed' : 'unchanged',
    comparedFields,
    changedFields,
  };
}

export class DatasetSyncController {
  private readonly documentTarget: BrowserDocumentTarget | null;
  private readonly windowTarget: BrowserEventTarget | null;
  private readonly setIntervalFn: DatasetSetInterval;
  private readonly clearIntervalFn: DatasetClearInterval;
  private readonly setTimeoutFn: DatasetSetTimeout;
  private readonly clearTimeoutFn: DatasetClearTimeout;
  private readonly stallTimeoutMs: number;
  private readonly retryDelaysMs: number[];
  private readonly slowReadRetryDelaysMs: number[];
  /** How many passes in a row ran out of time. */
  private consecutiveSlowReads = 0;
  private timer: DatasetIntervalHandle | null = null;
  private retryTimer: DatasetTimeoutHandle | null = null;
  private consecutiveFailures = 0;
  private hydrating = false;
  private lifecycleRevision = 0;
  private running = false;
  private queued = false;
  private queuedReason: DatasetSyncReason = 'follow-up';
  private stopped = true;
  /** Each pass and hydrate under a stall deadline, by how to abandon it; stop abandons them all. */
  private readonly inFlight = new Set<() => void>();

  constructor(private readonly options: DatasetSyncControllerOptions) {
    this.documentTarget =
      options.documentTarget === undefined
        ? typeof document === 'undefined'
          ? null
          : document
        : options.documentTarget;
    this.windowTarget =
      options.windowTarget === undefined
        ? typeof window === 'undefined'
          ? null
          : window
        : options.windowTarget;
    this.setIntervalFn =
      options.setIntervalFn ??
      ((callback, intervalMs) => globalThis.setInterval(callback, intervalMs));
    this.clearIntervalFn = options.clearIntervalFn ?? ((timer) => globalThis.clearInterval(timer));
    this.setTimeoutFn =
      options.setTimeoutFn ?? ((callback, delayMs) => globalThis.setTimeout(callback, delayMs));
    this.clearTimeoutFn = options.clearTimeoutFn ?? ((timer) => globalThis.clearTimeout(timer));
    this.stallTimeoutMs = options.stallTimeoutMs ?? DATASET_SYNC_STALL_TIMEOUT_MS;
    this.retryDelaysMs = options.retryDelaysMs ?? DATASET_SYNC_RETRY_DELAYS_MS;
    this.slowReadRetryDelaysMs =
      options.slowReadRetryDelaysMs ?? DATASET_SYNC_SLOW_READ_RETRY_DELAYS_MS;
  }

  /** How many passes in a row have failed; zero once one succeeds. */
  get failureStreak(): number {
    return this.consecutiveFailures;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.consecutiveFailures = 0;
    this.consecutiveSlowReads = 0;
    const lifecycleRevision = ++this.lifecycleRevision;
    this.documentTarget?.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.windowTarget?.addEventListener('focus', this.handleFocus);
    this.windowTarget?.addEventListener('online', this.handleOnline);
    this.windowTarget?.addEventListener(DATASET_SYNC_RECOVERY_EVENT, this.handleServiceRecovered);
    this.timer = this.setIntervalFn(() => {
      if (this.documentTarget?.visibilityState === 'hidden') return;
      void this.request('interval');
    }, this.options.intervalMs);
    if (this.options.hydrate && !this.options.raceHydrateWithVerify) {
      this.hydrating = true;
      void this.hydrateAndVerify(lifecycleRevision);
    } else {
      if (this.options.hydrate) void this.hydrateOnly(lifecycleRevision);
      void this.request('initial');
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.lifecycleRevision += 1;
    this.hydrating = false;
    this.queued = false;
    // I-1510: a stopped controller owns no timer and reports nothing. The passes still out are
    // abandoned, which ends their stall deadlines, and the latch they held is released, so a restart
    // asks at once instead of waiting out a pass whose answer it will drop.
    for (const abandon of [...this.inFlight]) abandon();
    this.running = false;
    this.documentTarget?.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.windowTarget?.removeEventListener('focus', this.handleFocus);
    this.windowTarget?.removeEventListener('online', this.handleOnline);
    this.windowTarget?.removeEventListener(
      DATASET_SYNC_RECOVERY_EVENT,
      this.handleServiceRecovered,
    );
    if (this.timer) this.clearIntervalFn(this.timer);
    this.timer = null;
    this.clearRetry();
  }

  async request(reason: DatasetSyncReason = 'manual'): Promise<void> {
    if (this.stopped) return;
    // I-1305: a read that ran out of time is waiting for its retry. Asking it again because the
    // poll came round, or the tab got focus, is the cadence the wait exists to end.
    if (this.consecutiveSlowReads > 0 && !ASKED_FOR_REASONS.has(reason)) return;
    if (this.hydrating) {
      this.queued = true;
      this.queuedReason = reason;
      return;
    }
    if (this.running) {
      this.queued = true;
      this.queuedReason = reason;
      return;
    }

    // The run this pass belongs to. A pass that answers after a stop, or after the restart that
    // followed it, is dropped: it notes nothing, schedules nothing, and leaves the latch to the new
    // run's own pass (I-1510).
    const lifecycleRevision = this.lifecycleRevision;
    const current = () => !this.stopped && lifecycleRevision === this.lifecycleRevision;
    this.running = true;
    try {
      await this.withStallDeadline(
        () => this.options.verify(reason),
        reason,
        // I-2006: a pass written off at its deadline whose read then answers. The deadline's task
        // can run before the task that delivers an answer already in hand, and the page applies
        // that answer on its own; it is a recovery, not a failure whose retry still falls due.
        () => {
          if (current()) this.noteSuccess();
        },
      );
      if (current()) this.noteSuccess();
    } catch (error) {
      // A maintenance interruption is a deliberate quiet stop, not a failure to recover from: the
      // deploy that caused it publishes a recovery event when it is done. Counting it would put the
      // controller into backoff for something that is already scheduled to resolve itself.
      if (current() && !isMaintenanceInterruptionError(error)) this.noteFailure(error, reason);
    } finally {
      if (lifecycleRevision === this.lifecycleRevision) this.running = false;
    }

    if (!current() || !this.queued) return;
    const nextReason = this.queuedReason;
    this.queued = false;
    this.queuedReason = 'follow-up';
    await this.request(nextReason);
  }

  /**
   * Runs one pass under a deadline it cannot outlive.
   *
   * A pass that exceeds the deadline is not cancelled — nothing here can cancel it — it is
   * *abandoned*: the deadline rejects, the caller releases its latch, and if the abandoned work
   * settles later its outcome is dropped. That trade is deliberate. A late answer applied out of
   * order is a bounded, self-correcting wrong; a latch held forever is neither.
   *
   * A stop abandons it the same way, before its deadline: the deadline is cleared and the promise
   * resolves empty, for a caller that checks its run is still current before using it (I-1510).
   */
  private withStallDeadline<T>(
    work: () => Promise<T> | T,
    reason: DatasetSyncReason,
    onLateAnswer?: () => void,
  ): Promise<T | undefined> {
    if (this.stallTimeoutMs <= 0) return Promise.resolve(work());
    return new Promise<T | undefined>((resolve, reject) => {
      let settled = false;
      const abandon = () => finish(() => resolve(undefined));
      const deadline = this.setTimeoutFn(() => {
        if (settled) return;
        settled = true;
        this.inFlight.delete(abandon);
        reject(new DatasetSyncStallError(reason, this.stallTimeoutMs));
      }, this.stallTimeoutMs);
      const finish = (act: () => void) => {
        if (settled) return;
        settled = true;
        this.inFlight.delete(abandon);
        this.clearTimeoutFn(deadline);
        act();
      };
      this.inFlight.add(abandon);
      // `Promise.resolve().then(work)` rather than calling `work()` directly so a synchronous throw
      // lands on the rejection path instead of escaping past the deadline that is meant to cover it.
      Promise.resolve()
        .then(work)
        .then(
          (value) => {
            // Written off already, and now answered: the deadline's rejection stands for the caller,
            // and the answer is reported on its own (a stop has already ended the run).
            if (settled && !this.stopped) onLateAnswer?.();
            finish(() => resolve(value));
          },
          (error) => finish(() => reject(error)),
        );
    });
  }

  private noteSuccess(): void {
    this.clearRetry();
    if (this.consecutiveFailures === 0) return;
    const failures = this.consecutiveFailures;
    this.consecutiveFailures = 0;
    this.consecutiveSlowReads = 0;
    this.options.onRecovered?.(failures);
  }

  private noteFailure(error: unknown, reason: DatasetSyncReason): void {
    this.consecutiveFailures += 1;
    this.consecutiveSlowReads =
      this.options.isSlowRead?.(error) === true ? this.consecutiveSlowReads + 1 : 0;
    // Scheduled before it is reported, so the report can say when the next pass is.
    const retryInMs = this.scheduleRetry();
    this.options.onError?.(error, reason, retryInMs);
  }

  /**
   * The unattended half of recovery.
   *
   * The interval poll would eventually retry on its own, but only while the tab is visible and only
   * at the dataset's own cadence, which is tuned for how often data changes rather than for how
   * quickly a failure should be re-tested. This schedules the re-test explicitly so a page left
   * showing a failure repairs itself without anyone pressing anything.
   */
  private scheduleRetry(): number | null {
    const slow = this.consecutiveSlowReads > 0;
    const delays = slow ? this.slowReadRetryDelaysMs : this.retryDelaysMs;
    if (this.stopped || delays.length === 0) return null;
    this.clearRetry();
    const failures = slow ? this.consecutiveSlowReads : this.consecutiveFailures;
    const delayMs = delays[Math.min(failures - 1, delays.length - 1)];
    this.retryTimer = this.setTimeoutFn(() => {
      this.retryTimer = null;
      void this.request('retry');
    }, delayMs);
    return delayMs;
  }

  private clearRetry(): void {
    if (this.retryTimer === null) return;
    this.clearTimeoutFn(this.retryTimer);
    this.retryTimer = null;
  }

  /**
   * The racing half of {@link DatasetSyncControllerOptions.raceHydrateWithVerify}: hydrate for its
   * own sake, with no pass sequenced behind it. It holds no latch, so however long it takes, it
   * delays nothing.
   */
  private async hydrateOnly(lifecycleRevision: number): Promise<void> {
    try {
      await this.withStallDeadline(() => this.options.hydrate?.(), 'initial');
    } catch (error) {
      if (this.stopped || lifecycleRevision !== this.lifecycleRevision) return;
      // Not counted as a sync failure: the remote pass is running alongside this one and is the
      // one that decides whether the dataset is healthy. A cache that could not be read is a lost
      // optimisation, not a lost answer.
      if (!isMaintenanceInterruptionError(error)) this.options.onError?.(error, 'initial', null);
    }
  }

  private async hydrateAndVerify(lifecycleRevision: number): Promise<void> {
    const superseded = () => this.stopped || lifecycleRevision !== this.lifecycleRevision;
    try {
      // Under the same deadline as a verify. A hydrate reads local storage, which is not supposed to
      // hang — but it holds the latch that gates every remote pass behind it, so "not supposed to"
      // is not a strong enough guarantee to leave unbounded.
      await this.withStallDeadline(() => this.options.hydrate?.(), 'initial');
    } catch (error) {
      if (!isMaintenanceInterruptionError(error)) this.options.onError?.(error, 'initial', null);
    } finally {
      // Nothing returns from in here: a `return` inside `finally` discards whatever the block was
      // unwinding, so an `onError` that threw would vanish instead of surfacing. The guard that
      // stops a superseded run belongs after the block, where it only skips the follow-up.
      if (!superseded()) this.hydrating = false;
    }

    if (superseded()) return;
    const nextReason = this.queued ? this.queuedReason : 'initial';
    this.queued = false;
    this.queuedReason = 'follow-up';
    await this.request(nextReason);
  }

  private readonly handleVisibilityChange: EventListener = () => {
    if (this.documentTarget?.visibilityState === 'visible') {
      void this.request('visible');
    }
  };

  private readonly handleFocus: EventListener = () => {
    if (this.documentTarget?.visibilityState !== 'hidden') {
      void this.request('focus');
    }
  };

  private readonly handleOnline: EventListener = () => {
    void this.request('online');
  };

  private readonly handleServiceRecovered: EventListener = () => {
    void this.request('service-recovered');
  };
}

function hasCheckpointValue(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function normalizeCheckpointValue(
  field: DatasetCheckpointField,
  value: string | number | null | undefined,
): string | number | null | undefined {
  if (field !== 'updatedAt' || typeof value !== 'string') return value;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? value : timestamp;
}
